import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PlusIcon } from "@radix-ui/react-icons";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@libs/zapi-ui/shadcn";
import { AppButton, FigmaTypography } from "@libs/zapi-ui/custom";
import { ChatShell } from "@/zapi-frontend/shared/ui/chat";
import { TChapter } from "@/zapi-frontend/shared/types/projects";
import { MODEL_CHOOSER_OPTIONS } from "@/zapi-frontend/shared/types/models";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { useChatStore } from "@/zapi-frontend/stores/chatStore";
import { useSnippetsVersionInfo } from "@/zapi-frontend/shared/hooks/useVersionInfo";
import { hasEditAccess } from "@/zapi-frontend/shared/utils/permissions";
import { ChatMessageList, TGenerationProgressProps } from "./ChatMessageList";
import { useChat } from "../hooks/useChat";
import { useFileUpload } from "../hooks/useFileUpload";

type TChatIslandProps = {
  webLinkEnabled?: boolean;
  messageAttachmentsEnabled?: boolean;
  chapter?: TChapter;
  askOnly?: boolean;
  generationProgress?: TGenerationProgressProps;
};

export function ChatIsland({
  webLinkEnabled = false,
  messageAttachmentsEnabled = false,
  chapter,
  askOnly,
  generationProgress
}: TChatIslandProps = {}) {
  const { t } = useTranslation(["projectHome", "common"]);
  const {
    projectId,
    messages,
    isLoading,
    loadingMessageId,
    isGeneratingChapter,
    progressMessage,
    prereqsChaptersToBeGenerated,
    allPrereqChapters,
    hasMessages,
    handleSubmit,
    handleAbortRequest,
    handleClearChat,
    isInitializing,
    isSendingRequest,
    isAbortingRequest,
    isAskStreaming
  } = useChat({ chapter, askOnly });

  const { isLabs, role, accessMode } = useMeStore();
  const hasEdit = hasEditAccess(role, accessMode);
  const { selectedProject } = useProjectStore();
  const { openFileDialog, attachmentFileInputRef, handleFileInputChange } = useFileUpload(projectId);
  const { modelName, setModelName } = useChatStore();
  const { displayText: versionDisplayText } = useSnippetsVersionInfo();

  const [input, setInput] = useState("");

  const chatEnabled = !!selectedProject?.chatEnabled;
  const disabled = isInitializing || !chatEnabled;

  const submit = () => {
    const messageText = input.trim();
    if (!messageText || isLoading || isSendingRequest || disabled) return;
    setInput("");
    handleSubmit(messageText, isLabs ? modelName : null);
  };

  const placeholder = (() => {
    if (isInitializing) return t("common:loadingChapter");
    return chatEnabled ? t("projectHome:chat.askAnything") : t("projectHome:chat.chatDisabled");
  })();

  return (
    <ChatShell
      className="h-full w-full"
      bodyClassName="p-0 overflow-hidden"
      title={t("projectHome:chat.title")}
      placeholder={placeholder}
      input={input}
      onInputChange={setInput}
      onSubmit={submit}
      isBusy={isLoading || isSendingRequest || disabled}
      submitLabel={t("common:submit")}
      inputTestId="chat-input"
      sendTestId="send-message-button"
      clearTestId="clear-chat-button"
      topBar={
        isLabs ? (
          <div className="flex items-center gap-2 mb-3 px-1">
            <FigmaTypography variant="body-3" className="tracking-wide">
              Model
            </FigmaTypography>
            <Select value={modelName} onValueChange={setModelName}>
              <SelectTrigger
                id="model-select"
                className="h-6 w-auto min-w-18 gap-2 px-1 text-[10px]"
                data-testid="model-select-trigger"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MODEL_CHOOSER_OPTIONS.map((m) => (
                  <SelectItem key={m.value} value={m.value}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : undefined
      }
      controlsLeft={
        <>
          {hasEdit && (
            <AppButton
              size="sm"
              type="button"
              color="zinc100"
              onClick={openFileDialog}
              disabled={isLoading || disabled}
              data-testid="add-files-button"
            >
              <PlusIcon /> {t("common:addFiles")}
            </AppButton>
          )}
          {isLabs && versionDisplayText && (
            <Tooltip>
              <TooltipTrigger>
                <FigmaTypography
                  variant="body-3"
                  className="text-[12px] leading-[18px] text-gray-500 font-mono truncate max-w-[85px]"
                  data-testid="version-info"
                >
                  {versionDisplayText}
                </FigmaTypography>
              </TooltipTrigger>
              <TooltipContent>
                <FigmaTypography variant="body-3">{versionDisplayText}</FigmaTypography>
              </TooltipContent>
            </Tooltip>
          )}
          <input
            type="file"
            ref={attachmentFileInputRef}
            accept=".pdf,.pptx,.jpg,.jpeg,.png,.txt,.csv,.xlsx,.xls,.xlsm,.docx"
            style={{ display: "none" }}
            multiple
            onChange={handleFileInputChange}
            data-testid="file-input"
          />
        </>
      }
      controls={
        isLoading && !isInitializing && !isSendingRequest ? (
          <AppButton
            color="red400"
            size="sm"
            type="button"
            onClick={handleAbortRequest}
            data-testid="abort-button"
            isLoading={isAbortingRequest}
          >
            {t("common:cancel")}
          </AppButton>
        ) : undefined
      }
      onClear={handleClearChat}
      canClear={hasMessages && !isLoading && !disabled}
      clearConfirm={{ title: t("common:clearChat"), description: t("projectHome:chat.clearChatConfirm") }}
    >
      <ChatMessageList
        isInitializing={isInitializing}
        messages={messages}
        loadingMessageId={loadingMessageId}
        isGeneratingChapter={isGeneratingChapter}
        progressMessage={progressMessage}
        prereqsChaptersToBeGenerated={prereqsChaptersToBeGenerated}
        allPrereqChapters={allPrereqChapters}
        isAskStreaming={isAskStreaming}
        webLinkEnabled={webLinkEnabled}
        messageAttachmentsEnabled={messageAttachmentsEnabled}
        generationProgress={generationProgress}
      />
    </ChatShell>
  );
}
